/**
 * @spec Figma Design System - Dropdown (node-id: 22961:92432)
 * Reusable dropdown component following Phenom design system
 */
import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { DropdownItem as DropdownItemComponent } from './DropdownItem';
import ChevronDownIcon from '@/assets/icons/chevron-down.svg?react';

export interface DropdownItem<T = string> {
  /** Unique identifier for the item */
  id: T;
  /** Primary label to display */
  label: ReactNode;
  /** Optional icon to display before the label */
  icon?: ReactNode;
  /** Optional secondary label (e.g., wallet type) */
  secondaryLabel?: string;
  /** Optional trailing content (e.g., checkmark) */
  trailing?: ReactNode;
  /** Whether item is disabled */
  disabled?: boolean;
}

export interface DropdownProps<T = string> {
  /** The dropdown items */
  items: DropdownItem<T>[];
  /** Currently selected item id */
  selectedId?: T;
  /** Callback when an item is selected */
  onSelect: (id: T) => void;
  /** Trigger element content */
  trigger: ReactNode;
  /** Custom className for the container */
  className?: string;
  /** Custom className for the trigger button */
  triggerClassName?: string;
  /** Whether to show chevron indicator */
  showChevron?: boolean;
  /** Alignment of dropdown relative to trigger */
  align?: 'left' | 'right';
  /** Accessible label for the dropdown */
  ariaLabel?: string;
}

/**
 * Dropdown component following Figma design system
 *
 * Features:
 * - Semi-transparent backdrop with blur
 * - Purple-tinted border glow
 * - 44px height items with proper padding
 * - Text states: 50% white default, 100% white on hover/selected
 * - Hover background: 3% white (from Figma)
 * - Click outside or Escape to close
 * - Keyboard accessible
 */
export function Dropdown<T = string>({
  items,
  selectedId,
  onSelect,
  trigger,
  className,
  triggerClassName,
  showChevron = true,
  align = 'right',
  ariaLabel = 'Select option',
}: DropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleSelect = useCallback(
    (id: T) => {
      onSelect(id);
      setIsOpen(false);
    },
    [onSelect]
  );

  const toggleDropdown = useCallback(() => {
    if (items.length > 1) {
      setIsOpen((prev) => !prev);
    }
  }, [items.length]);

  const hasMultipleItems = items.length > 1;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Trigger Button */}
      <button
        onClick={toggleDropdown}
        className={cn(
          'flex items-center gap-2',
          hasMultipleItems && 'cursor-pointer',
          triggerClassName
        )}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        type="button"
      >
        {trigger}
        {showChevron && hasMultipleItems && (
          <ChevronDownIcon
            className={cn('h-4 w-4 text-text-muted transition-transform', isOpen && 'rotate-180')}
          />
        )}
      </button>

      {/* Dropdown Menu - Figma Design System */}
      {isOpen && hasMultipleItems && (
        <div
          className={cn(
            'absolute top-full mt-2 min-w-[200px] z-50',
            'backdrop-blur-[5px] bg-bg-primary/95',
            'rounded-lg',
            'shadow-[0px_10px_40px_0px_rgba(0,0,0,0.6)]',
            align === 'right' ? 'right-0' : 'left-0'
          )}
          role="listbox"
          aria-label={ariaLabel}
        >
          <div
            className={cn('relative ', 'border border-0 border-box border-glow', 'rounded-lg py-2')}
          >
            {items.map((item, index) => {
              const isSelected = item.id === selectedId;
              const isFirst = index === 0;
              const isLast = index === items.length - 1;

              return (
                <DropdownItemComponent
                  key={String(item.id)}
                  onClick={() => !item.disabled && handleSelect(item.id)}
                  disabled={item.disabled}
                  selected={isSelected}
                  icon={item.icon}
                  secondaryLabel={item.secondaryLabel}
                  trailing={item.trailing}
                  isFirst={isFirst}
                  isLast={isLast}
                  role="option"
                  aria-selected={isSelected}
                >
                  {item.label}
                </DropdownItemComponent>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
